import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export const TAB_SEARCH_PARAM = 'tab';

/**
 * Keeps the selected tab in the URL (`?tab=pipelines`) instead of component
 * state, so it survives a refresh and can be linked to directly.
 *
 * `availableTabs` guards against a URL naming a tab this page does not render —
 * pages build their tab list from the package type, so `?tab=findings` on a
 * package without findings would otherwise leave the content area blank. While
 * the page is still loading its tab list is empty, so the parameter is trusted
 * until the real list arrives rather than being discarded on the first render.
 */
export const useTabParam = <T extends string>(defaultTab: T, availableTabs: readonly T[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const param = searchParams.get(TAB_SEARCH_PARAM) as T | null;
  const isKnownTab =
    Boolean(param) && (availableTabs.length === 0 || availableTabs.includes(param as T));
  const selectedTab = isKnownTab ? (param as T) : defaultTab;

  const setSelectedTab = useCallback(
    (tab: T) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set(TAB_SEARCH_PARAM, tab);
          return next;
        },
        // Switching tabs replaces the entry so Back leaves the page rather than
        // stepping through every tab the user clicked.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [selectedTab, setSelectedTab] as const;
};
